<template>
  <div class="bm-records">
    <div class="bm-records__tags">
      <button v-for="tag in tags" :key="tag.label" :class="{ active: activeTag === tag.label }" @click="changeTag(tag)">
        {{ tag.label }}
      </button>
      <button :disabled="loading" @click="load()">刷新</button>
    </div>
    <div class="bm-records__table-wrap">
      <table>
        <thead>
          <tr>
            <th>修改内容</th>
            <th>处理进度</th>
            <th>状态</th>
            <th>排位位置</th>
            <th>成功数</th>
            <th>失败数</th>
            <th>跳过数</th>
            <th>取消数</th>
            <th>商品总数</th>
            <th>时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="record in records" :key="record.id">
            <td>
              <strong>{{ record.type_name }}</strong>
              <small>{{ record.rule }}</small>
            </td>
            <td>
              <div class="bm-records__progress">
                <i :style="{ width: record.progress + '%' }"></i>
              </div>
              <b>{{ record.progress }}%</b>
            </td>
            <td>
              <span class="bm-records__status" :class="statusClass(record.status)">{{ record.status_name }}</span>
            </td>
            <td>
              <template v-if="record.queue_position != null && [1, 2].includes(Number(record.status))">
                第 {{ record.queue_position }} 位
                <small>前方 {{ record.queue_ahead_count }} 个待处理任务</small>
              </template>
              <template v-else>—</template>
            </td>
            <td class="success">{{ record.success_count }}</td>
            <td class="danger">{{ record.fail_count }}</td>
            <td>{{ record.skip_count }}</td>
            <td>{{ record.cancelled_count == null ? '—' : record.cancelled_count }}</td>
            <td>{{ record.total }}</td>
            <td>
              <small>
                创建 {{ record.created_at || '—' }}
                <br />
                计划 {{ record.scheduled_at || '立即执行' }}
              </small>
            </td>
            <td class="bm-records__actions">
              <button class="blue" @click="openDetail(record)">查看详情</button>
              <button
                v-if="record.cancel_supported"
                class="danger"
                :disabled="cancellingIds.includes(record.id)"
                @click="cancelRecord(record)">
                {{ cancellingIds.includes(record.id) ? '取消中…' : '取消' }}
              </button>
              <button
                v-if="record.restore_supported && ![7, 8].includes(Number(record.type))"
                class="blue"
                :disabled="restoringIds.includes(record.id)"
                @click="restoreRecord(record)">
                {{
                  restoringIds.includes(record.id)
                    ? '恢复中…'
                    : Number(record.type) === 5
                    ? '一键恢复属性'
                    : Number(record.type) === 7
                    ? '一键恢复上下架'
                    : '一键恢复货号'
                }}
              </button>
              <small v-if="Number(record.status) === 11 && Number(record.pending_confirmation_count) > 0">
                已停止后续修改，另有 {{ record.pending_confirmation_count }} 个已提交结果待确认
              </small>
            </td>
          </tr>
          <tr v-if="!records.length && !loading">
            <td colspan="11" class="empty">暂无记录数据</td>
          </tr>
        </tbody>
      </table>
      <footer>
        <span>共 {{ pageState.total }} 条 · 共 {{ totalPages }} 页</span>
        <span>
          <button :disabled="pageState.page <= 1 || loading" @click="load(pageState.page - 1)">上一页</button>
          <b>{{ pageState.page }} / {{ totalPages }}</b>
          <button :disabled="pageState.page >= totalPages || loading" @click="load(pageState.page + 1)">下一页</button>
          <select v-model.number="pageState.limit" @change="changeLimit">
            <option :value="10">10条/页</option>
            <option :value="20">20条/页</option>
            <option :value="50">50条/页</option>
          </select>
        </span>
      </footer>
    </div>
    <div v-if="detail" class="bm-records__mask">
      <div class="bm-records__modal">
        <header>
          <strong>{{ detail.type_name }}修改详情</strong>
          <button @click="closeDetail">×</button>
        </header>
        <div class="bm-records__modal-body">
          <div v-if="Number(detail.status) === 11" class="bm-records__queue">
            已取消，停止 {{ detail.cancelled_count == null ? '—' : detail.cancelled_count }} 个商品任务
            <small v-if="Number(detail.pending_confirmation_count) > 0">
              另有 {{ detail.pending_confirmation_count }} 个已提交结果待确认，已提交到抖店的修改无法撤回
            </small>
          </div>
          <template v-if="detailLoading"><div class="empty">加载中…</div></template>
          <template v-else-if="detailUnsupported">
            <div class="empty">{{ detailUnsupported }}</div>
          </template>
          <template v-else>
            <nav>
              <button :class="{ active: detailStatus === '' }" @click="changeDetailStatus('')">全部</button>
              <button
                :class="{ active: detailStatus === 9 && (!allowsSkip || detailSkipped === '0') }"
                @click="changeDetailStatus(9, '0')">
                成功
              </button>
              <button :class="{ active: detailStatus === 10 }" @click="changeDetailStatus(10)">失败</button>
              <button
                v-if="allowsSkip"
                :class="{ active: detailStatus === 9 && detailSkipped === '1' }"
                @click="changeDetailStatus(9, '1')">
                跳过
              </button>
              <button :class="{ active: detailStatus === 8 }" @click="changeDetailStatus(8)">待确认</button>
              <button :class="{ active: detailStatus === 11 }" @click="changeDetailStatus(11)">已取消</button>
              <button :disabled="detailLoading" @click="loadDetail()">刷新</button>
            </nav>
            <table>
              <thead>
                <tr>
                  <th>商品ID</th>
                  <th>平台商品ID</th>
                  <th>修改前{{ detailColumn.label }}</th>
                  <th>修改后{{ detailColumn.label }}</th>
                  <th>状态</th>
                  <th class="bm-records__error-column">失败原因</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in detailRows" :key="item.id">
                  <td>{{ item.distribute_product_id }}</td>
                  <td>{{ item.platform_product_id || '—' }}</td>
                  <td>
                    {{ detailValue(item, false) }}
                    <template v-if="detailSchema === 'fabric'">
                      <el-image
                        v-for="url in fabricImages(item, false)"
                        :key="url"
                        style="width: 64px; height: 64px"
                        :src="url"
                        :preview-src-list="fabricImages(item, false)"
                        fit="cover" />
                    </template>
                  </td>
                  <td>
                    {{ detailValue(item, true) }}
                    <template v-if="detailSchema === 'fabric'">
                      <el-image
                        v-for="url in fabricImages(item, true)"
                        :key="url"
                        style="width: 64px; height: 64px"
                        :src="url"
                        :preview-src-list="fabricImages(item, true)"
                        fit="cover" />
                      <small v-if="item.attribute_reasons">
                        {{
                          { pending: '素材审核中', approved: '素材审核通过', failed: '素材审核失败' }[
                            item.attribute_reasons.audit_status
                          ] || ''
                        }}
                      </small>
                    </template>
                  </td>
                  <td>
                    {{
                      item.is_skipped
                        ? '已跳过'
                        : allowsSkip && Number(item.status) === 9
                        ? '已受理'
                        : detailStatusName(item.status)
                    }}
                  </td>
                  <td class="danger bm-records__error-column">
                    <span class="bm-records__error-text" :title="detailReason(item)">
                      {{ detailReason(item) || '—' }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!detailRows.length">
                  <td colspan="6" class="empty">暂无明细</td>
                </tr>
              </tbody>
            </table>
            <footer>
              <span>共 {{ detailPage.total }} 条 · 共 {{ detailTotalPages }} 页</span>
              <el-pagination
                class="bm-records__detail-pagination"
                small
                layout="prev, pager, next, jumper"
                :pager-count="5"
                :current-page="detailPage.page"
                :page-size="detailPage.limit"
                :total="detailPage.total"
                :disabled="detailLoading"
                @current-change="loadDetail" />
              <button @click="closeDetail">关闭</button>
            </footer>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getDoudianBatchDetail,
  getDoudianBatchList,
  cancelDoudianBatch,
  restoreDoudianAttributeBatch,
  restoreDoudianHuohaoBatch,
  restoreDoudianShelfBatch
} from '@/api/main/doudianBatchEdit'

const typeMap = { 标题: 1, 导购短标题: 2, 品牌: 3, 货号: 4, 商品属性: 5, 面料材质: 6, 上下架: 7, 定时上下架: 8 }
// 抖店标准品牌库中"无品牌"的固定品牌 ID。
const NO_BRAND_ID = '596120136'
// 明细列按后端 detail_schema 渲染；新增业务类型在此登记列名、取值方法与是否支持跳过。
const detailColumns = {
  title: { label: '标题', value: 'titleValue', allowsSkip: false },
  short_title: { label: '短标题', value: 'shortTitleValue', allowsSkip: true },
  brand: { label: '品牌', value: 'brandValue', allowsSkip: true },
  attribute: { label: '属性', value: 'attributeValue', allowsSkip: true },
  fabric: { label: '面料/图片', value: 'fabricValue', allowsSkip: true },
  huohao: { label: '货号', value: 'huohaoValue', allowsSkip: true },
  shelf: { label: '上下架', value: 'shelfValue', allowsSkip: true }
}
const tags = [
  '全部',
  '标题',
  '导购短标题',
  '品牌',
  '商品属性',
  '跨类目属性',
  '面料材质',
  '货号',
  '删除商品',
  '下架/删除低效品',
  '上下架',
  '定时上下架',
  '创建渠道品',
  '限时限量购',
  '食品安全/营养成分表',
  '价格(SKU)',
  '库存',
  'SKU编码',
  'SKU规格名称/备注',
  '添加SKU',
  '删除SKU',
  '发货模式(SKU时效)',
  '预售时间',
  '商品详情图',
  '主图',
  '主图3:4',
  '库存计件',
  '尺码表',
  '区域库存开关',
  'SKU状态',
  'SKU规格图',
  'SKU图',
  '替换商品图片',
  '统一设置SKU',
  '白底图',
  '导入修改(多字段)',
  'SKU条形码（国补码）',
  '引用SKU模板',
  '多地址退货策略',
  '精选联盟策略',
  '主图视频',
  '运费模板',
  '商品限购',
  '服务资质',
  '7天无理由退换货',
  '大件商品',
  '三包服务承诺',
  '虚拟商品',
  '安装模式',
  '综合修改(多字段修改)',
  '价格-库存-SKU编码'
].map((label) => ({
  label,
  type: label === '全部' ? '' : typeMap[label] || 0
}))
const statusNames = {
  3: '待同步详情',
  4: '处理中',
  5: '图片上传中',
  6: '图片审核中',
  7: '待提交修改',
  8: '待确认平台结果',
  9: '已完成',
  10: '失败',
  11: '已取消'
}

export default {
  name: 'BatchModifyRecords',
  data() {
    return {
      tags,
      activeTag: '全部',
      activeType: '',
      records: [],
      loading: false,
      queueSummary: {},
      cancellingIds: [],
      restoringIds: [],
      listRequestId: 0,
      detailRequestId: 0,
      pageState: { page: 1, limit: 10, total: 0 },
      detail: null,
      detailRows: [],
      detailStatus: '',
      detailSkipped: '',
      detailSchema: '',
      refreshTimer: null,
      refreshing: false,
      detailPage: { page: 1, limit: 20, total: 0 },
      detailLoading: false,
      detailUnsupported: ''
    }
  },
  computed: {
    detailColumn() {
      return detailColumns[this.detailSchema || this.detail?.detail_schema] || detailColumns.title
    },
    allowsSkip() {
      return this.detailColumn.allowsSkip
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.pageState.total / this.pageState.limit))
    },
    detailTotalPages() {
      return Math.max(1, Math.ceil(this.detailPage.total / this.detailPage.limit))
    }
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer)
    this.listRequestId++
    this.detailRequestId++
  },
  created() {
    this.load()
  },
  methods: {
    scheduleRefresh() {
      clearTimeout(this.refreshTimer)
      if (this._isDestroyed) return
      const pending = (item) =>
        item && (![9, 10, 11].includes(Number(item.status)) || Number(item.pending_confirmation_count) > 0)
      if (!this.records.some(pending) && !pending(this.detail) && !(Number(this.queueSummary.waiting_count) > 0)) return
      this.refreshTimer = setTimeout(async () => {
        if (!this.loading && !this.detailLoading && !this.refreshing) {
          this.refreshing = true
          try {
            await this.load(this.pageState.page, true)
            if (this.detail) await this.loadDetail(this.detailPage.page, true)
          } finally {
            this.refreshing = false
          }
        }
        this.scheduleRefresh()
      }, 10000)
    },
    shortTitleValue(item, after) {
      if (!item.value_ready) return after && item.operation === 'clear' ? '计划清空（待计算）' : '待同步详情'
      if (after && item.operation === 'clear')
        return Number(item.status) === 9 && !item.is_skipped ? '已受理清空' : '计划清空'
      const value = after ? item.new_short_title : item.old_short_title
      return value === null || value === undefined || value === '' ? '未设置' : value
    },
    brandValue(item, after) {
      const id = after ? item.new_brand_id : item.old_brand_id
      if (String(id) === NO_BRAND_ID) return '无品牌'
      return (after ? item.new_brand_name : item.old_brand_name) || '品牌名称待同步'
    },
    attributeValue(item, after) {
      if (item.common_only) return '属性未修改，仅处理上下架/尺码表'
      const values = after ? item.new_property_json : item.old_property_json
      if (!values || !Object.keys(values).length) return '无修改项或待同步详情'
      return Object.keys(values)
        .map(
          (id) =>
            ((item.attribute_names || {})[id] || id) +
            '：' +
            (values[id].present
              ? values[id].rows
                  .map((row) => row.name || row.Name || String(row.value === undefined ? row.Value || '' : row.value))
                  .join('、')
              : after
              ? '清空属性内容'
              : '未设置')
        )
        .join('；')
    },
    shelfValue(item, after) {
      if (!after) return item.old_platform_status_name || '待同步'
      const time = [item.online_at ? '上架 ' + item.online_at : '', item.offline_at ? '下架 ' + item.offline_at : '']
        .filter(Boolean)
        .join('；')
      return [item.action_name, item.result_platform_status_name, time, item.virtual_offline ? '虚拟下架' : '']
        .filter(Boolean)
        .join(' ')
    },
    huohaoValue(item, after) {
      if (item.skip_reason === 'property_not_supported') return after ? '不适用' : '类目无货号属性'
      if (item.value_ready === false) return '待同步详情'
      const value = after ? item.new_huohao : item.old_huohao
      return value === null || value === undefined || value === '' ? '未设置' : value
    },
    titleValue(item, after) {
      return this.detailTitle(after ? item.new_title : item.old_title, item.status)
    },
    detailValue(item, after) {
      return this[this.detailColumn.value](item, after)
    },
    fabricValue(item, after) {
      const value = after ? item.new_property_json : item.old_property_json
      const rows = value && (after ? value.new_composition : value.composition)
      return Array.isArray(rows) && rows.length
        ? rows.map((row) => row.name + ' ' + row.percent + '%').join('、')
        : '未记录'
    },
    fabricImages(item, after) {
      const value = after ? item.new_property_json : item.old_property_json
      const urls = value && (after ? value.new_images : value.images)
      return Array.isArray(urls) ? urls.filter((url) => /^https:/.test(url)) : []
    },
    detailReason(item) {
      return (
        item.error_msg ||
        (this.detailSchema === 'fabric' ? '' : Object.values(item.attribute_reasons || {}).join('；')) ||
        item.match_reason ||
        item.brand_eligibility_reason ||
        {
          property_not_supported: '类目不支持货号属性，已过滤',
          restore_expired: '已超过2天恢复期限，已跳过',
          already_restored: '已恢复为原值，已跳过',
          restore_conflict: '商品属性或类目已变化，为避免覆盖已跳过',
          category_not_editable: '本地类目定义不可用，已跳过',
          property_requires_image: '待恢复属性现已要求图片，只能保持原值，已跳过',
          category_changed: '商品类目已变化，已跳过',
          all_attributes_unchanged: '属性无需修改且未选择上下架或自动尺码表，已跳过',
          no_change: '修改前后内容一致，已跳过',
          condition_not_matched: '未命中修改条件，已跳过'
        }[item.skip_reason] ||
        ''
      )
    },
    responseData(response) {
      const root = response && response.code !== undefined ? response : response?.data
      if (Number(root?.code) !== 200) throw new Error(root?.message || root?.msg || '请求失败')
      return root.data || {}
    },
    async cancelRecord(record) {
      if (this.cancellingIds.includes(record.id)) return
      this.cancellingIds.push(record.id)
      try {
        await this.$confirm('取消后将停止后续修改，已提交到抖店的修改无法撤回，是否继续？', '取消批量修改', {
          confirmButtonText: '确认取消',
          cancelButtonText: '继续执行',
          type: 'warning',
          customClass: 'bm-records-cancel-confirm'
        })
      } catch (_) {
        this.cancellingIds = this.cancellingIds.filter((id) => id !== record.id)
        return
      }
      if (this._isDestroyed) return
      let responseReceived = false
      try {
        const response = await cancelDoudianBatch(record.id)
        responseReceived = true
        const data = this.responseData(response)
        if (this._isDestroyed) return
        this.listRequestId++
        this.detailRequestId++
        this.detailLoading = false
        const patch = { ...data, cancel_supported: false, queue_ahead_count: null, queue_position: null }
        this.records = this.records.map((item) => (item.id === record.id ? { ...item, ...patch } : item))
        if (this.detail?.id === record.id) this.detail = { ...this.detail, ...patch }
        this.$message.success('任务已取消')
        await this.load(this.pageState.page, false, '取消已成功，记录刷新失败，请稍后刷新')
        if (this.detail?.id === record.id) await this.loadDetail(this.detailPage.page)
      } catch (error) {
        if (!this._isDestroyed) {
          this.$message.error(
            !responseReceived && !error?.response
              ? '取消结果待核实，请刷新记录'
              : error?.response?.data?.message || error?.message || '取消失败，请刷新记录'
          )
          await this.load(this.pageState.page, true)
        }
      } finally {
        this.cancellingIds = this.cancellingIds.filter((id) => id !== record.id)
        this.scheduleRefresh()
      }
    },
    async restoreRecord(record) {
      if (this.restoringIds.includes(record.id)) return
      this.restoringIds.push(record.id)
      try {
        const type = Number(record.type)
        const label = type === 5 ? '属性' : type === 7 ? '上下架' : '货号'
        await this.$confirm('将按原批次成功修改的' + label + '创建恢复任务，是否继续？', '一键恢复' + label, {
          confirmButtonText: '创建恢复任务',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const request =
          type === 5 ? restoreDoudianAttributeBatch : type === 7 ? restoreDoudianShelfBatch : restoreDoudianHuohaoBatch
        const data = this.responseData(await request(record.id))
        this.$message.success('恢复任务已创建：' + data.batch_no)
        await this.load(this.pageState.page)
      } catch (error) {
        if (error !== 'cancel' && error !== 'close')
          this.$message.error(error?.response?.data?.message || error?.message || '恢复任务创建失败')
      } finally {
        this.restoringIds = this.restoringIds.filter((id) => id !== record.id)
      }
    },
    async load(page = this.pageState.page, background = false, failureMessage = '') {
      const requestId = ++this.listRequestId
      this.loading = true
      try {
        const params = { page, limit: this.pageState.limit }
        if (this.activeType !== '') params.type = this.activeType
        const data = this.responseData(await getDoudianBatchList(params))
        if (requestId !== this.listRequestId || this._isDestroyed) return
        this.records = Array.isArray(data.list) ? data.list : []
        this.queueSummary = data.queue_summary || {}
        if (this.detail) {
          const current = this.records.find((item) => item.id === this.detail.id)
          if (current) this.detail = current
        }
        this.pageState = { ...this.pageState, page, total: Number(data.total || 0) }
      } catch (error) {
        if (!background && requestId === this.listRequestId && !this._isDestroyed)
          this.$message.error(failureMessage || error?.response?.data?.message || error?.message || '记录加载失败')
      } finally {
        if (requestId === this.listRequestId) this.loading = false
        this.scheduleRefresh()
      }
    },
    changeTag(tag) {
      this.activeTag = tag.label
      this.activeType = tag.type
      this.load(1)
    },
    changeLimit() {
      this.load(1)
    },
    statusClass(status) {
      return { 9: 'completed', 10: 'failed', 11: 'cancelled' }[Number(status)] || 'processing'
    },
    detailStatusName(status) {
      return statusNames[Number(status)] || '处理中'
    },
    detailTitle(title, status) {
      return Number(status) === 3 && (title === '' || title === null || title === undefined) ? '抖店标题同步中' : title
    },
    async openDetail(record) {
      this.detailRequestId++
      this.detail = record
      this.detailUnsupported = record.detail_supported ? '' : '该类型详情暂未接入'
      this.detailRows = []
      this.detailStatus = ''
      this.detailSkipped = ''
      this.detailSchema = record.detail_schema || ''
      this.detailPage = { page: 1, limit: 20, total: 0 }
      if (record.detail_supported) await this.loadDetail(1)
    },
    async loadDetail(page = this.detailPage.page, background = false) {
      if (!this.detail) return
      const batchId = this.detail.id
      const requestId = ++this.detailRequestId
      if (!background) this.detailLoading = true
      this.detailUnsupported = ''
      try {
        const params = { page, limit: this.detailPage.limit }
        if (this.detailStatus !== '') params.status = this.detailStatus
        if (this.detailSkipped !== '') params.is_skipped = this.detailSkipped
        const data = this.responseData(await getDoudianBatchDetail(batchId, params))
        if (requestId !== this.detailRequestId || this.detail?.id !== batchId || this._isDestroyed) return
        this.detailRows = Array.isArray(data.list) ? data.list : []
        this.detail = {
          ...this.detail,
          cancelled_count: data.cancelled_count,
          pending_confirmation_count: data.pending_confirmation_count
        }
        this.detailSchema = data.detail_schema || this.detail.detail_schema || ''
        this.detailPage = { ...this.detailPage, page, total: Number(data.total || 0) }
      } catch (error) {
        if (!background && requestId === this.detailRequestId && this.detail?.id === batchId && !this._isDestroyed)
          this.detailUnsupported = error?.response?.data?.message || error?.message || '详情加载失败'
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
        this.scheduleRefresh()
      }
    },
    changeDetailStatus(status, skipped = '') {
      this.detailStatus = status
      this.detailSkipped = this.allowsSkip ? skipped : ''
      this.loadDetail(1)
    },
    closeDetail() {
      this.detailRequestId++
      this.detailLoading = false
      this.detail = null
      this.detailRows = []
      this.detailUnsupported = ''
      this.detailSchema = ''
      this.scheduleRefresh()
    }
  }
}
</script>

<style>
.bm-records-cancel-confirm {
  max-width: calc(100vw - 32px);
}
</style>

<style scoped>
.bm-records {
  padding: 14px 20px;
  font-family: 'Noto Sans SC', sans-serif;
}
.bm-records__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
}
.bm-records__queue {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f5f7fa;
  color: #606266;
  font-size: 12px;
}
.bm-records__queue span {
  margin-left: 16px;
}
.bm-records__queue small {
  display: block;
  margin-top: 6px;
  color: #909399;
}
.bm-records__actions {
  min-width: 150px;
}
.bm-records__actions button {
  margin: 2px 4px 2px 0;
  white-space: nowrap;
}
.bm-records__status.cancelled {
  background: #f0f0f0;
  color: #909399;
}
.bm-records button,
.bm-records select {
  height: 26px;
  padding: 0 10px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  cursor: pointer;
  font-size: 11px;
}
.bm-records button.active {
  border-color: #1677ff;
  background: #1677ff;
  color: #fff;
}
.bm-records button:disabled {
  color: #bbb;
  cursor: not-allowed;
}
.bm-records__table-wrap,
.bm-records__modal {
  overflow: hidden;
  border: 1px solid #e0e0ec;
  border-radius: 8px;
  background: #fff;
}
.bm-records__table-wrap {
  overflow-x: auto;
}
.bm-records__modal {
  overflow-x: auto;
}
.bm-records table {
  width: 100%;
  min-width: 1060px;
  border-collapse: collapse;
}
.bm-records th {
  padding: 8px 12px;
  background: #f5f0e8;
  color: #666;
  font-size: 11px;
  text-align: left;
}
.bm-records td {
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 12px;
}
.bm-records td small {
  display: block;
  margin-top: 3px;
  color: #999;
  font-size: 11px;
  line-height: 1.5;
}
.bm-records__progress {
  display: inline-block;
  width: 90px;
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background: #f0f0f0;
}
.bm-records__progress i {
  display: block;
  height: 100%;
  background: #e05858;
}
.bm-records td > b {
  margin-left: 6px;
  color: #c8443c;
  font-size: 10px;
}
.bm-records__status {
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
}
.bm-records__status.completed {
  background: #f6ffed;
  color: #52c41a;
}
.bm-records__status.failed {
  background: #fff5f5;
  color: #c8443c;
}
.bm-records__status.processing {
  background: #fff7e6;
}
.bm-records__status.processing {
  color: #fa8c16;
}
.success {
  color: #52c41a;
}
.danger {
  color: #c8443c;
}
.blue {
  border-color: #91caff !important;
  background: #e6f4ff !important;
  color: #1677ff !important;
}
.bm-records footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #f5f0e8;
  color: #999;
  font-size: 11px;
}
.bm-records__modal footer {
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}
.bm-records__detail-pagination {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  padding: 0;
}
.bm-records__detail-pagination ::v-deep .el-pagination__jump {
  margin-left: 0;
}
.bm-records__mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.4);
}
.bm-records__modal {
  display: flex;
  flex-direction: column;
  width: min(1000px, 100%);
  max-height: 85vh;
  overflow: hidden;
}
.bm-records__modal-body {
  min-height: 0;
  overflow: auto;
}
.bm-records__modal > header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px solid #e0e0ec;
}
.bm-records__modal nav {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 18px;
}
.bm-records__modal nav button {
  flex-shrink: 0;
  white-space: nowrap;
}
.bm-records__modal table {
  width: calc(100% - 36px);
  margin: 0 18px;
  min-width: 700px;
}
.bm-records__error-column {
  width: 180px;
  max-width: 180px;
}
.bm-records__error-text {
  display: block;
  width: 180px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.empty {
  padding: 28px !important;
  text-align: center;
  color: #999;
}
</style>
